import { useEffect, useMemo, useState } from 'react';
import { magazinesService } from '../services/magazinesService';
import { Magazine } from '../types/trimestersSettings.types';

export const useMagazines = (trimesterId?: string) => {
  const [magazines, setMagazines] = useState<Magazine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    setIsLoading(true);

    magazinesService
      .list(trimesterId)
      .then((items) => {
        if (!mounted) return;
        setMagazines(items);
        setError(null);
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(loadError instanceof Error ? loadError.message : 'Nao foi possivel carregar as revistas.');
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [trimesterId]);

  const saveMagazine = async (input: Omit<Magazine, 'id'> & { id?: string }) => {
    const magazine: Magazine = {
      id: input.id ?? `mag-${Date.now()}`,
      title: input.title,
      trimesterId: input.trimesterId,
      segmentId: input.segmentId,
      description: input.description,
      lessons: input.lessons,
    };

    const saved = input.id ? await magazinesService.update(magazine) : await magazinesService.create(magazine);
    setMagazines((current) => {
      if (input.id) {
        return current.map((item) => (item.id === input.id ? saved : item));
      }

      return [saved, ...current];
    });
  };

  const removeMagazine = async (magazineId: string) => {
    await magazinesService.remove(magazineId);
    setMagazines((current) => current.filter((item) => item.id !== magazineId));
  };

  const loadImportFields = () => magazinesService.importFields();
  const previewImport = (file: File) => magazinesService.previewImport(file);

  const importLessons = async (input: { file: File; trimesterId: string; mapping: Record<string, string> }) => {
    const result = await magazinesService.importLessons(input);
    const items = await magazinesService.list();
    setMagazines(items);

    return result;
  };

  const getMagazineForSegment = useMemo(
    () => (segmentId: string, trimesterId?: string) =>
      magazines.find((magazine) => magazine.segmentId === segmentId && (!trimesterId || magazine.trimesterId === trimesterId)) ??
      magazines.find((magazine) => magazine.segmentId === segmentId) ??
      null,
    [magazines]
  );

  return {
    magazines,
    isLoading,
    error,
    saveMagazine,
    removeMagazine,
    getMagazineForSegment,
    loadImportFields,
    previewImport,
    importLessons,
  };
};
